import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminInvoiceService from "../../services/admin/AdminInvoiceService";
import {
  ArrowLeft,
  Download,
  Share2,
  ArrowRightLeft,
  CheckCircle2,
  FileText,
  ChevronRight,
  MoreVertical,
  AlertCircle,
  Loader2,
  Edit2,
  Check,
  X,
  MapPin,
  Map,
  Calendar,
  Maximize2,
  Mail,
  MessageCircle,
} from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { useComingSoon } from "../../utils/useComingSoon";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showComingSoon } = useComingSoon();

  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const shareDropdownRef = useRef(null);

  // Close share dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(e.target)) {
        setShowShareDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchInvoiceDetail();
  }, [id]);

  const fetchInvoiceDetail = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const result = await AdminInvoiceService.getDetail(id);
      if (result.status === "success") {
        setInvoice(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch invoice details:", error);
      showNotification({
        type: "failed",
        title: "Load Error",
        message: "Failed to load invoice details. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const statusMap = {
    generated: {
      label: "Generated",
      color: "text-gray-600 bg-gray-100 border-gray-200",
      next: ["approved", "cancelled"],
    },
    approved: {
      label: "Approved",
      color: "text-teal-600 bg-teal-50 border-teal-100",
      next: ["in_use", "cancelled"],
    },
    in_use: {
      label: "In Use",
      color: "text-purple-600 bg-purple-50 border-purple-100",
      next: ["completed"],
    },
    completed: {
      label: "Completed",
      color: "text-green-600 bg-green-50 border-green-100",
      next: [],
    },
    cancelled: {
      label: "Cancelled",
      color: "text-red-600 bg-red-50 border-red-100",
      next: [],
    },
  };

  const handleStatusChange = async (newStatus) => {
    if (updatingStatus) return;
    setUpdatingStatus(newStatus);

    try {
      const result = await AdminInvoiceService.updateStatus(id, newStatus);
      if (result.status === "success") {
        // Update local state directly with enriched data (includes activity log and status)
        setInvoice(result.data);

        setShowStatusModal(false);
        showNotification({
          type: "success",
          title: "Status Updated",
          message: `Invoice status changed to ${statusMap[newStatus].label}`
        });
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      showNotification({
        type: "failed",
        title: "Update Error",
        message: error.response?.data?.message || "Failed to update status."
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleEmailUpdate = async () => {
    if (!newEmail.trim()) return;
    setIsUpdatingEmail(true);
    try {
      const result = await AdminInvoiceService.updateEmail(id, newEmail);
      if (result.status === "success") {
        // Update local state directly with enriched data from server (includes activity log)
        setInvoice(result.data);

        setIsEditingEmail(false);
        showNotification({
          type: "success",
          title: "Email Updated",
          message: "Customer email has been updated correctly."
        });
      }
    } catch (error) {
      console.error("Failed to update email:", error);
      showNotification({
        type: "failed",
        title: "Update Error",
        message: error.response?.data?.message || "Failed to update email."
      });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const response = await AdminInvoiceService.download(id);
      const blob = response.data;

      if (!blob || blob.size === 0) {
        showNotification({
          type: "info",
          title: "File Unavailable",
          message: "The invoice PDF could not be found. It may still be generating."
        });
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `St-Stephens-Rentals-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: "success",
        title: "Download Started",
        message: "Your invoice is being downloaded."
      });
    } catch (error) {
      console.error("Download error:", error);
      showNotification({
        type: "failed",
        title: "Download Failed",
        message: "Failed to download the invoice. Please try again."
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShareEmail = async () => {
    if (isSharing) return;
    setShowShareDropdown(false);
    setIsSharing(true);

    showNotification({
      type: "info",
      title: "Sharing Invoice",
      message: `Sending ${invoice.invoice_number} via email to ${invoice.customer?.email || "the customer"}...`
    });

    try {
      const result = await AdminInvoiceService.share(id);
      if (result.status === "success") {
        showNotification({
          type: "success",
          title: "Invoice Sent",
          message: result.message || `Invoice sent successfully.`
        });
        // Refresh to show the activity log update
        fetchInvoiceDetail(true);
      }
    } catch (error) {
      console.error("Share error:", error);
      showNotification({
        type: "failed",
        title: "Share Failed",
        message: error.response?.data?.message || "Failed to share the invoice. Please try again."
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleShareWhatsApp = () => {
    setShowShareDropdown(false);
    showComingSoon({
      title: "WhatsApp Sharing Coming Soon!",
      message: "We're building WhatsApp sharing for invoices. Stay tuned!"
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="animate-spin text-[#62397f]" size={40} />
        </div>
      </AdminLayout>
    );
  }

  if (!invoice) {
    return (
      <AdminLayout>
        <div className="p-8 text-center space-y-4">
          <AlertCircle size={48} className="mx-auto text-red-500" />
          <h2 className="text-xl font-bold dark:text-white">Invoice Not Found</h2>
          <button
            onClick={() => location.state?.fromCreate ? navigate("/admin/invoices") : navigate(-1)}
            className="text-purple-600 font-semibold"
          >
            Go Back
          </button>
        </div>
      </AdminLayout>
    );
  }


  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-[1000px] mx-auto space-y-8 pb-20">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => location.state?.fromCreate ? navigate("/admin/invoices") : navigate(-1)}
              className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-xl transition-colors"
            >
              <ArrowLeft
                size={20}
                className="text-[#6B7280] dark:text-[#A0A0A0]"
              />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
                  {invoice.invoice_number}
                </h1>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusMap[invoice.status].color}`}
                >
                  {statusMap[invoice.status].label}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#666666] dark:text-[#A0A0A0] text-sm">
                  Customer:{" "}
                  <span
                    onClick={() => navigate(`/admin/customers/${invoice.customer?.id}`)}
                    className="font-semibold text-[#111111] dark:text-white cursor-pointer hover:text-[#62397f] transition-colors"
                  >
                    {invoice.customer.name}
                  </span>
                </p>
                <div className="flex items-center gap-2 group">
                  {isEditingEmail ? (
                    <div className="flex flex-wrap items-center gap-2 w-full max-w-full">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        disabled={isUpdatingEmail}
                        className="text-xs px-2 py-1.5 bg-white dark:bg-[#262626] border border-purple-200 dark:border-purple-900/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 flex-1 min-w-[200px] max-w-full"
                        placeholder="Enter email address"
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleEmailUpdate}
                          disabled={isUpdatingEmail || !newEmail}
                          className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                        >
                          {isUpdatingEmail ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        </button>
                        <button
                          onClick={() => setIsEditingEmail(false)}
                          disabled={isUpdatingEmail}
                          className="p-1.5 bg-gray-100 dark:bg-[#262626] text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#666666] dark:text-[#A0A0A0] text-sm break-all max-w-full">
                        {invoice.customer.email ? (
                          <span className="font-medium">{invoice.customer.email}</span>
                        ) : (
                          <span className="italic text-gray-400">No email provided</span>
                        )}
                      </p>
                      {invoice.is_admin_generated && (
                        <button
                          onClick={() => {
                            setNewEmail(invoice.customer.email || "");
                            setIsEditingEmail(true);
                          }}
                          className="p-1.5 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/10 rounded-lg transition-all border border-purple-100 dark:border-purple-900/30 shadow-sm"
                          title="Edit Email"
                        >
                          <Edit2 size={12} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap w-full md:w-auto">
            <button
              onClick={handleDownload}
              disabled={isDownloading || !!updatingStatus}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-medium text-[#4B5563] dark:text-[#A0A0A0] hover:bg-white dark:hover:bg-[#262626] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={16} className={isDownloading ? "animate-bounce" : ""} />
              <span className="md:hidden lg:inline">{isDownloading ? "Preparing..." : "Download"}</span>
            </button>
            <div className="relative" ref={shareDropdownRef}>
              <button
                onClick={() => setShowShareDropdown(prev => !prev)}
                disabled={isSharing || isDownloading || !!updatingStatus}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-medium text-[#4B5563] dark:text-[#A0A0A0] hover:bg-white dark:hover:bg-[#262626] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSharing ? (
                  <Loader2 size={16} className="animate-spin text-[#62397f]" />
                ) : (
                  <Share2 size={16} />
                )}
                <span className="md:hidden lg:inline">{isSharing ? "Sharing..." : "Share"}</span>
              </button>
              {showShareDropdown && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                  <button
                    onClick={handleShareEmail}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#111111] dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-[#262626] transition-colors"
                  >
                    <Mail size={16} className="text-[#62397f]" />
                    Send via Email
                  </button>
                  <button
                    onClick={handleShareWhatsApp}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#111111] dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-[#262626] transition-colors border-t border-[#E5E7EB] dark:border-[#2A2A2A]"
                  >
                    <MessageCircle size={16} className="text-green-500" />
                    Send via WhatsApp
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowStatusModal(true)}
              disabled={isDownloading || !!updatingStatus}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-xl text-sm font-bold shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
            >
              <ArrowRightLeft size={16} />
              Update Status
            </button>
          </div>
        </div>

        {/* Invoice PDF Viewer */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden shadow-sm h-[500px] md:h-[650px] lg:h-[800px] relative">
          <iframe
            src={`${invoice.pdf_url}#toolbar=0`}
            className="w-full h-full border-none"
            title="Invoice PDF Viewer"
          />
          <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex gap-3">
            <a
              href={invoice.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 md:p-3 bg-white/90 dark:bg-[#1A1A1A]/90 backdrop-blur-sm rounded-full border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white hover:bg-[#62397f] hover:text-white transition-colors shadow-lg"
              title="Open Fullscreen"
            >
              <Maximize2 size={18} className="md:w-5 md:h-5" />
            </a>
          </div>
        </div>

        {/* Info Grid (Event Details) - Only for User Generated Invoices */}
        {invoice.is_user_generated && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-lg">
                  <Calendar size={18} />
                </div>
                <h3 className="text-sm font-bold text-[#111111] dark:text-white font-poppins">Event Type</h3>
              </div>
              <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed">
                {invoice.event_type || "General Rental"}
              </p>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg">
                  <Map size={18} />
                </div>
                <h3 className="text-sm font-bold text-[#111111] dark:text-white font-poppins">Location Level</h3>
              </div>
              <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed capitalize">
                {(invoice.event_location_level || "").replace("_", " ")}
              </p>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-lg">
                  <MapPin size={18} />
                </div>
                <h3 className="text-sm font-bold text-[#111111] dark:text-white font-poppins">Venue Address</h3>
              </div>
              <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed">
                {invoice.event_venue_address || "None specified"}
              </p>
            </div>
          </div>
        )}

        {/* Audit Log / History */}
        <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
          <h3 className="text-sm font-bold text-[#111111] dark:text-white font-poppins mb-6">
            Activity Log
          </h3>
          <div className="space-y-6">
            {invoice.activity_log.map((log, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${log.action.includes("Status") ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"
                    } dark:bg-opacity-20`}>
                    {log.action.includes("Status") ? <ArrowRightLeft size={14} /> : <FileText size={14} />}
                  </div>
                  {idx < invoice.activity_log.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gray-100 dark:bg-gray-800 my-1"></div>
                  )}
                </div>
                <div className={idx < invoice.activity_log.length - 1 ? "pb-6" : ""}>
                  <p className="text-sm font-bold text-[#111111] dark:text-white leading-none mb-1">
                    {log.action}
                  </p>
                  <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0] mb-2">
                    {new Date(log.created_at).toLocaleString()} • by {log.admin_name}
                  </p>
                  {log.details && (
                    <p className="text-xs bg-gray-50 dark:bg-[#262626] p-2 rounded-lg border border-gray-100 dark:border-[#333333] italic text-gray-500">
                      "{log.details}"
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden">
            <div className="p-6 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
              <h3 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                Update Invoice Status
              </h3>
              <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                Current:{" "}
                <span className="font-bold">{statusMap[invoice.status].label}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-[#9CA3AF] uppercase">
                  Next Valid Status
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {statusMap[invoice.status].next.length > 0 ? (
                    statusMap[invoice.status].next.map((nextStatus) => (
                      <button
                        key={nextStatus}
                        onClick={() => handleStatusChange(nextStatus)}
                        disabled={!!updatingStatus}
                        className={`flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-[#2A2A2A] hover:border-[#62397f] hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <span className="text-sm font-bold text-[#111111] dark:text-white">
                          {statusMap[nextStatus].label}
                        </span>
                        {updatingStatus === nextStatus ? (
                          <Loader2 size={18} className="animate-spin text-[#62397f]" />
                        ) : (
                          <ChevronRight
                            size={18}
                            className="text-gray-300 group-hover:text-[#62397f]"
                          />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-[#262626] rounded-2xl border border-dashed border-gray-200 text-center">
                      <p className="text-sm text-gray-500 italic">
                        No further status updates available
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 bg-[#F9FAFB] dark:bg-[#262626] flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 py-3 text-sm font-bold text-[#6B7280] dark:text-[#A0A0A0] hover:text-[#111111] dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
